import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    requestAnimationFrame(() => {
      this.#maintainExclusiveAccordion();
    });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Label link navigates; gap / + icon still toggles the accordion.
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('a.menu-drawer__menu-item-link');
    if (link instanceof HTMLAnchorElement && this.summary.contains(link)) {
      event.preventDefault();
      const href = link.getAttribute('href');
      if (href && href !== '#') {
        window.location.assign(href);
      }
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
    requestAnimationFrame(() => {
      this.#maintainExclusiveAccordion();
    });
  };

  /**
   * Closes other accordion rows in the same product-details (or FAQ) accordion group.
   */
  #handleToggle = () => {
    if (!this.details.open) return;
    this.#closeSiblingAccordions();
  };

  /**
   * Ensures only one row stays open when multiple rows are marked open by default.
   */
  #maintainExclusiveAccordion() {
    const group = this.closest('.accordion');
    if (!group) return;

    const items = [...group.querySelectorAll('accordion-custom')];
    const openItems = items.filter((item) => item instanceof AccordionCustom && item.details.open);

    if (openItems.length <= 1) return;

    openItems.slice(1).forEach((item) => {
      item.details.open = false;
    });
  }

  /**
   * Closes sibling accordion rows in the same exclusive group.
   */
  #closeSiblingAccordions() {
    const group = this.closest('.accordion');
    if (!group) return;

    group.querySelectorAll('accordion-custom').forEach((accordion) => {
      if (accordion === this) return;
      if (!(accordion instanceof AccordionCustom)) return;
      if (accordion.details.open) accordion.details.open = false;
    });
  }

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
